import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Star, Clock, Wind, Sparkles, Sun, Moon, Snowflake, Flower2, ShoppingCart, Play, Pause, ChevronDown } from 'lucide-react';

const API = `${window.location.origin}/api`;

const LONGEVITY_LABELS = { poor: 'Poor (1-2h)', moderate: 'Moderate (3-5h)', long: 'Long (6-8h)', very_long: 'Very Long (8-12h)', beast: 'Beast Mode (12h+)' };
const LONGEVITY_PCT = { poor: 15, moderate: 35, long: 55, very_long: 75, beast: 95 };
const SILLAGE_LABELS = { intimate: 'Intimate', moderate: 'Moderate', strong: 'Strong', enormous: 'Enormous' };
const SILLAGE_PCT = { intimate: 20, moderate: 45, strong: 70, enormous: 95 };
const SEASON_ICONS = { winter: Snowflake, spring: Flower2, summer: Sun, fall: Wind, all_season: Star };

export default function PerfumeDetailPage() {
    const { slug, perfumeId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showVideo, setShowVideo] = useState(true);
    const videoRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/catalog/${slug}/perfume/${perfumeId}`).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
    }, [slug, perfumeId]);

    // Force autoplay on mount and when showVideo changes
    useEffect(() => {
        if (videoRef.current && showVideo) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(() => {});
        }
    }, [showVideo, data]);

    if (loading) return <div className="min-h-screen bg-[#080810] flex items-center justify-center"><motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
    if (!data) return <div className="min-h-screen bg-[#080810] flex items-center justify-center text-white/30 text-sm">Perfume not found</div>;

    const { shop, perfume: p } = data;
    const primary = shop.primary_color || '#D4AF37';
    const bg = shop.background_color || '#080810';
    const font = shop.font_family || 'Cormorant Garamond';
    const topNotes = p.top_notes ? p.top_notes.split(',').map(n => n.trim()).filter(Boolean) : [];
    const midNotes = p.middle_notes ? p.middle_notes.split(',').map(n => n.trim()).filter(Boolean) : [];
    const baseNotes = p.base_notes ? p.base_notes.split(',').map(n => n.trim()).filter(Boolean) : [];
    const accords = p.main_accords || [];
    const hasVideo = !!p.video_url;
    const SeasonIcon = SEASON_ICONS[p.season] || Star;

    const stagger = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

    return (
        <div className="min-h-screen pb-28" style={{ backgroundColor: bg, fontFamily: `'${font}', serif` }} data-testid="perfume-detail-page">
            <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600;700&display=swap`} />

            {/* Back */}
            <div className="sticky top-0 z-10 px-4 py-3 backdrop-blur-2xl" style={{ backgroundColor: `${bg}CC` }}>
                <button onClick={() => navigate(`/catalog/${slug}`)} className="flex items-center gap-2 text-white/30 text-xs uppercase tracking-wider hover:text-white/50 transition-colors" data-testid="back-to-catalog"><ArrowLeft className="w-4 h-4" /> Back to Collection</button>
            </div>

            <div className="max-w-lg mx-auto px-4">
                {/* Hero Media */}
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative mb-8">
                    {hasVideo ? (
                        <div className="relative rounded-3xl overflow-hidden aspect-[3/4] bg-black">
                            {showVideo ? (
                                <video
                                    ref={videoRef}
                                    src={p.video_url}
                                    poster={p.image_url}
                                    className="w-full h-full object-cover"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    webkit-playsinline=""
                                    preload="auto"
                                />
                            ) : p.image_url ? (
                                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-white/[0.02]"><Sparkles className="w-20 h-20 text-white/5" /></div>
                            )}
                            {/* Video play button */}
                            <button
                                onClick={() => setShowVideo(!showVideo)}
                                className="absolute bottom-5 right-5 w-14 h-14 rounded-full flex items-center justify-center border transition-all hover:scale-105"
                                style={{ backgroundColor: `${bg}CC`, borderColor: `${primary}40`, backdropFilter: 'blur(12px)' }}
                            >
                                {showVideo ? <Pause className="w-5 h-5" style={{ color: primary }} /> : <Play className="w-5 h-5 ml-0.5" style={{ color: primary }} />}
                            </button>
                            {/* Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                        </div>
                    ) : p.image_url ? (
                        <div className="rounded-3xl overflow-hidden aspect-[3/4]">
                            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="rounded-3xl overflow-hidden aspect-[3/4] flex items-center justify-center bg-white/[0.02] border border-white/[0.04]">
                            <Sparkles className="w-20 h-20 text-white/5" />
                        </div>
                    )}
                </motion.div>

                {/* Title Section */}
                <motion.div {...stagger} transition={{ delay: 0.1 }} className="text-center mb-8">
                    <p className="text-[10px] text-white/20 uppercase tracking-[0.3em] mb-2">{p.brand}</p>
                    <h1 className="text-4xl font-light text-white leading-tight">{p.name}</h1>
                    <div className="flex items-center justify-center gap-4 mt-4">
                        <span className="text-3xl font-light" style={{ color: primary }}>QAR {p.price}</span>
                        {p.gender && (
                            <span className="px-3 py-1 rounded-full text-[9px] uppercase tracking-wider border" style={{ color: `${primary}CC`, borderColor: `${primary}30` }}>
                                {p.gender === 'unisex' ? 'Unisex' : p.gender === 'male' ? 'For Him' : 'For Her'}
                            </span>
                        )}
                    </div>
                    {p.availability && (
                        <span className={`inline-block mt-3 px-3 py-1 rounded-full text-[9px] uppercase tracking-wider ${
                            p.availability === 'in_stock' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                            p.availability === 'limited' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                            'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>{p.availability.replace('_', ' ')}</span>
                    )}
                </motion.div>

                {p.description && (
                    <motion.p {...stagger} transition={{ delay: 0.15 }} className="text-sm text-white/35 text-center mb-10 leading-relaxed max-w-sm mx-auto italic">{p.description}</motion.p>
                )}

                {/* Main Accords */}
                {accords.length > 0 && (
                    <motion.div {...stagger} transition={{ delay: 0.2 }} className="mb-10">
                        <h3 className="text-[10px] text-white/20 uppercase tracking-[0.3em] text-center mb-5">Main Accords</h3>
                        <div className="space-y-3">
                            {accords.map((a, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <span className="text-xs text-white/50 w-20 text-right font-medium">{a.name}</span>
                                    <div className="flex-1 h-2 rounded-full overflow-hidden bg-white/[0.04]">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${a.strength || 50}%` }}
                                            transition={{ delay: 0.4 + i * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                            className="h-full rounded-full"
                                            style={{ backgroundColor: a.color || primary }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Fragrance Notes Pyramid */}
                {(topNotes.length > 0 || midNotes.length > 0 || baseNotes.length > 0) && (
                    <motion.div {...stagger} transition={{ delay: 0.25 }} className="mb-10">
                        <h3 className="text-[10px] text-white/20 uppercase tracking-[0.3em] text-center mb-6">Fragrance Pyramid</h3>
                        <div className="space-y-6">
                            {[{ label: 'Top', notes: topNotes, icon: '▲' }, { label: 'Heart', notes: midNotes, icon: '◆' }, { label: 'Base', notes: baseNotes, icon: '▼' }].map(group => group.notes.length > 0 && (
                                <div key={group.label}>
                                    <div className="flex items-center gap-3 justify-center mb-3">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent" style={{ backgroundImage: `linear-gradient(to right, transparent, ${primary}15)` }} />
                                        <span className="text-[10px] text-white/25 uppercase tracking-[0.2em] flex items-center gap-2">
                                            <span style={{ color: primary, fontSize: '8px' }}>{group.icon}</span> {group.label} Notes
                                        </span>
                                        <div className="h-px flex-1 bg-gradient-to-l from-transparent" style={{ backgroundImage: `linear-gradient(to left, transparent, ${primary}15)` }} />
                                    </div>
                                    <div className="flex flex-wrap justify-center gap-2">
                                        {group.notes.map((n, i) => (
                                            <span key={i} className="px-4 py-2 rounded-full text-xs text-white/50 border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors">{n}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Performance Meters */}
                {(p.longevity || p.sillage) && (
                    <motion.div {...stagger} transition={{ delay: 0.3 }} className="grid grid-cols-2 gap-4 mb-10">
                        {p.longevity && (
                            <div className="rounded-2xl bg-white/[0.02] border border-white/[0.04] p-5">
                                <div className="flex items-center gap-2 mb-4"><Clock className="w-4 h-4" style={{ color: primary }} /><span className="text-[10px] text-white/25 uppercase tracking-wider">Longevity</span></div>
                                <div className="relative h-2 rounded-full bg-white/[0.04] overflow-hidden mb-3">
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${LONGEVITY_PCT[p.longevity] || 50}%` }} transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ backgroundColor: primary, boxShadow: `0 0 12px ${primary}40` }} />
                                </div>
                                <p className="text-xs text-white/40 font-medium">{LONGEVITY_LABELS[p.longevity] || p.longevity}</p>
                            </div>
                        )}
                        {p.sillage && (
                            <div className="rounded-2xl bg-white/[0.02] border border-white/[0.04] p-5">
                                <div className="flex items-center gap-2 mb-4"><Wind className="w-4 h-4" style={{ color: primary }} /><span className="text-[10px] text-white/25 uppercase tracking-wider">Sillage</span></div>
                                <div className="relative h-2 rounded-full bg-white/[0.04] overflow-hidden mb-3">
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${SILLAGE_PCT[p.sillage] || 50}%` }} transition={{ delay: 0.7, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ backgroundColor: primary, boxShadow: `0 0 12px ${primary}40` }} />
                                </div>
                                <p className="text-xs text-white/40 font-medium">{SILLAGE_LABELS[p.sillage] || p.sillage}</p>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* When to Wear */}
                {(p.season || p.day_night) && (
                    <motion.div {...stagger} transition={{ delay: 0.35 }} className="flex justify-center gap-4 mb-10">
                        {p.season && (
                            <div className="flex flex-col items-center gap-2 px-6 py-4 rounded-2xl bg-white/[0.02] border border-white/[0.04]">
                                <SeasonIcon className="w-5 h-5" style={{ color: primary }} />
                                <span className="text-[10px] text-white/25 uppercase tracking-wider capitalize">{p.season.replace('_', ' ')}</span>
                            </div>
                        )}
                        {p.day_night && (
                            <div className="flex flex-col items-center gap-2 px-6 py-4 rounded-2xl bg-white/[0.02] border border-white/[0.04]">
                                {p.day_night === 'day' ? <Sun className="w-5 h-5" style={{ color: primary }} /> : p.day_night === 'night' ? <Moon className="w-5 h-5" style={{ color: primary }} /> : <Star className="w-5 h-5" style={{ color: primary }} />}
                                <span className="text-[10px] text-white/25 uppercase tracking-wider capitalize">{p.day_night}</span>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Rating */}
                {p.rating > 0 && (
                    <motion.div {...stagger} transition={{ delay: 0.4 }} className="text-center mb-10 rounded-2xl bg-white/[0.02] border border-white/[0.04] p-6">
                        <h3 className="text-[10px] text-white/20 uppercase tracking-[0.3em] mb-4">Rating</h3>
                        <div className="flex items-center justify-center gap-1.5 mb-3">
                            {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-7 h-7 ${s <= Math.round(p.rating) ? 'fill-current' : ''}`} style={{ color: s <= Math.round(p.rating) ? primary : 'rgba(255,255,255,0.08)' }} />
                            ))}
                        </div>
                        <p className="text-3xl font-light" style={{ color: primary }}>{p.rating}</p>
                        <p className="text-[10px] text-white/20 uppercase tracking-wider mt-1">out of 5</p>
                        {p.review_source && <p className="text-[9px] text-white/15 mt-3">Source: {p.review_source}</p>}
                    </motion.div>
                )}

                {/* Reviews */}
                {(p.expert_review || p.customer_review) && (
                    <motion.div {...stagger} transition={{ delay: 0.45 }} className="mb-10 space-y-4">
                        {p.expert_review && (
                            <div className="rounded-2xl bg-white/[0.02] border border-white/[0.04] p-6">
                                <h3 className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-3">Expert Review</h3>
                                <p className="text-sm text-white/45 leading-relaxed italic">"{p.expert_review}"</p>
                            </div>
                        )}
                        {p.customer_review && (
                            <div className="rounded-2xl bg-white/[0.02] border border-white/[0.04] p-6">
                                <h3 className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-3">Customer Review</h3>
                                <p className="text-sm text-white/45 leading-relaxed">"{p.customer_review}"</p>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* Similar */}
                {p.similar_perfumes && (
                    <motion.div {...stagger} transition={{ delay: 0.5 }} className="mb-10">
                        <h3 className="text-[10px] text-white/20 uppercase tracking-[0.3em] text-center mb-4">Similar Fragrances</h3>
                        <div className="flex flex-wrap justify-center gap-2">
                            {p.similar_perfumes.split(',').map((s, i) => (
                                <span key={i} className="px-4 py-2 rounded-full bg-white/[0.02] border border-white/[0.04] text-xs text-white/30">{s.trim()}</span>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Sticky Add to Cart */}
            <motion.div
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
                className="fixed bottom-0 left-0 right-0 p-4 backdrop-blur-2xl z-50"
                style={{ backgroundColor: `${bg}E6`, borderTop: `1px solid ${primary}15` }}
            >
                <div className="max-w-lg mx-auto">
                    <button
                        onClick={() => navigate(`/catalog/${slug}`)}
                        className="w-full py-4 rounded-2xl text-white font-medium tracking-wider uppercase text-sm flex items-center justify-center gap-3 transition-all hover:brightness-110"
                        style={{ backgroundColor: primary, boxShadow: `0 8px 24px ${primary}30` }}
                        data-testid="add-to-cart-detail-btn"
                    >
                        <ShoppingCart className="w-5 h-5" /> Add to Cart — QAR {p.price}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
